import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, Compass, Mail } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";
import { buildSeoMeta } from "@/lib/seo";

export const Route = createFileRoute("/login")({
  head: () => {
    const seo = buildSeoMeta({
      title: "Sign in — RanchPlanner",
      description: "Sign in with a magic link to save plans, log visits, and get personalized picks.",
      path: "/login",
    });
    return {
      ...seo,
      meta: [...seo.meta, { name: "robots", content: "noindex, nofollow" }],
    };
  },
  component: LoginPage,
});

function LoginPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/account" });
  }, [user, loading, navigate]);

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    const trimmed = email.trim();
    if (!trimmed || !/^\S+@\S+\.\S+$/.test(trimmed)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSending(true);
    try {
      const origin = typeof window !== "undefined" ? window.location.origin : undefined;
      const { error } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: {
          emailRedirectTo: origin ? `${origin}/account` : undefined,
        },
      });
      if (error) {
        toast.error(error.message || "Couldn't send magic link.");
        setSending(false);
        return;
      }
      setSent(true);
    } catch (err: any) {
      toast.error(err?.message || "Couldn't send magic link.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-sm text-center">
        <Link to="/" className="inline-flex items-center gap-2 mb-6">
          <Compass className="h-10 w-10 text-primary" />
        </Link>
        <h1 className="text-3xl font-black tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
          Sign in<span className="text-highlight">.</span>
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Save plans across devices, log places you've been, and get picks tuned to you.
        </p>
        {sent ? (
          <div className="mt-6 rounded-xl border-2 border-primary/30 bg-primary/5 p-5 text-left">
            <div className="flex items-center gap-2 font-bold text-foreground">
              <Mail className="h-5 w-5 text-primary" />
              Check your inbox
            </div>
            <p className="mt-2 text-sm text-muted-foreground">
              We sent a magic link to <span className="font-semibold text-foreground">{email}</span>. Tap it on this device to finish signing in. You'll stay signed in for about 30 days.
            </p>
            <button
              type="button"
              onClick={() => { setSent(false); }}
              className="mt-3 text-xs underline text-muted-foreground"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form onSubmit={handleMagicLink} className="mt-6 space-y-3 text-left">
            <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wide text-muted-foreground">
              Email address
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-4 py-3 rounded-xl border-2 border-foreground/15 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary transition"
            />
            <button
              type="submit"
              disabled={sending}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90 transition disabled:opacity-50"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
              Email me a magic link
            </button>
            <p className="text-xs text-muted-foreground text-center">
              No password needed. We'll send you a one-tap sign-in link.
            </p>
          </form>
        )}
        <p className="mt-6 text-xs text-muted-foreground">
          By signing in you agree to our{" "}
          <Link to="/legal/terms" className="underline">Terms</Link> and{" "}
          <Link to="/legal/privacy" className="underline">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}